import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { usePmTestMode } from "../hooks/usePmTestMode";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  TrendingUp, Copy, Send, FileText, ExternalLink, RefreshCw,
  CheckCircle2, Clock, Zap, DollarSign, ChevronDown, ChevronUp,
  AlertCircle, User, Calendar, Settings, Trash2, Pencil,
} from "lucide-react";
import { AddChangeOrderModal } from "../components/change-orders/AddChangeOrderModal";
import { RecordManualPaymentDialog } from "../components/change-orders/RecordManualPaymentDialog";
import { DeleteAdditionalScopeDialog } from "../components/change-orders/DeleteAdditionalScopeDialog";
import { canDeleteAdditionalScope, canEditAdditionalScope, isFinanceRole } from "../components/change-orders/additionalScopeAccess";

// ── Status pipeline for upsells ──────────────────────────────────────────────
const COLUMNS = [
  {
    key: "submitted",
    label: "Submitted",
    icon: Clock,
    color: "bg-slate-100 text-slate-700 border-slate-200",
    header: "bg-slate-50",
    statuses: ["ready_to_send", "draft"],
  },
  {
    key: "proposal_sent",
    label: "Proposal Sent",
    icon: Send,
    color: "bg-sky-100 text-sky-700 border-sky-200",
    header: "bg-sky-50",
    statuses: ["sent_to_client"],
  },
  {
    key: "in_production",
    label: "In Production",
    icon: Zap,
    color: "bg-amber-100 text-amber-700 border-amber-200",
    header: "bg-amber-50",
    statuses: ["client_approved", "paid", "cleared_for_production", "in_production"],
  },
  {
    key: "delivered",
    label: "Delivered",
    icon: CheckCircle2,
    color: "bg-violet-100 text-violet-700 border-violet-200",
    header: "bg-violet-50",
    statuses: ["completed"],
  },
  {
    key: "complete",
    label: "Fully Paid",
    icon: DollarSign,
    color: "bg-green-100 text-green-700 border-green-200",
    header: "bg-green-50",
    statuses: ["fully_paid"],
  },
];

const STATUS_LABEL = {
  draft: "Draft",
  ready_to_send: "Submitted",
  sent_to_client: "Proposal Sent",
  client_approved: "Approved",
  invoice_sent: "Invoice Sent",
  paid: "Deposit Paid",
  cleared_for_production: "Cleared",
  in_production: "In Production",
  completed: "Delivered",
  fully_paid: "Fully Paid",
};

const fmt$ = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Client proposal/payment links go inactive 7 days after (re)send.
const linkDaysRemaining = (u) => {
  if (!u.link_expires_at || u.status !== "sent_to_client") return null;
  const ms = new Date(u.link_expires_at).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

function columnFor(status) {
  return COLUMNS.find((c) => c.statuses.includes(status)) || COLUMNS[0];
}

export default function UpsellsDashboard() {
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [sending, setSending] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);
  // Manual payment recording: { co, paymentType: "deposit" | "balance" }
  const [manualPay, setManualPay] = useState(null);
  // Upsell pending deletion (confirmation dialog target)
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Upsell open in the wizard for editing (null = creating a new one)
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coRes, meRes] = await Promise.all([
        apiClient.get("/change-orders"),
        apiClient.get("/auth/me"),
      ]);
      const all = coRes.data?.change_orders || [];
      setUpsells(all.filter((co) => co.request_type === "upsell"));
      setMe(meRes.data);
    } catch {
      toast.error("Failed to load upsells");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const navigate = useNavigate();
  const pmTestMode = usePmTestMode();
  const isPM = me?.role === "Project Manager" || me?.role === "Program Manager" || me?.role === "Marketing PM";
  const isAdmin = me?.role === "COO" || me?.role === "Admin";
  const isSales = me?.role === "Sales";
  // Never show pricing to PMs, nor when an admin is previewing PM mode.
  // "Sales Assistant" is a quoting-only role: it sees prices here and in the
  // Client Calculator, and nowhere else in HQ (see permissions.QUOTE_PRICING_ROLES).
  const canShowPrice = (isAdmin || isSales || (me?.role === "Accountant") || (me?.role === "Finance") || (me?.role === "Sales Assistant")) && !pmTestMode.isActive;
  // Finance deletes anything; everyone else only their own not-yet-sent rows.
  // Dropping isFinance under PM preview makes the preview honest — an admin then
  // sees exactly the ownership-based Delete a PM or Sales rep would.
  const isFinance = isFinanceRole(me?.role) && !pmTestMode.isActive;

  // Group upsells into pipeline columns
  const grouped = useMemo(() => {
    const map = {};
    COLUMNS.forEach((c) => { map[c.key] = []; });
    upsells.forEach((u) => {
      const col = columnFor(u.status);
      map[col.key].push(u);
    });
    return map;
  }, [upsells]);

  // Stats
  const totalRevenue = upsells.reduce((s, u) => s + (u.final_client_price || u.total_amount || 0), 0);
  const depositCollected = upsells
    .filter((u) => u.deposit_paid)
    .reduce((s, u) => s + (u.deposit_amount || 0), 0);
  const inProgress = upsells.filter((u) => !["fully_paid", "draft"].includes(u.status)).length;

  const handleSendProposal = async (u) => {
    if (!u.client_email) {
      toast.error("No client email on this upsell — add one first");
      return;
    }
    setSending((p) => ({ ...p, [u.id]: true }));
    try {
      const res = await apiClient.post(`/change-orders/${u.id}/send-to-client`, {});
      toast.success("Proposal sent to " + u.client_email);
      if (res.data?.approval_url) {
        navigator.clipboard.writeText(res.data.approval_url);
        toast("Approval link copied to clipboard");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send proposal");
    } finally {
      setSending((p) => ({ ...p, [u.id]: false }));
    }
  };

  const handleCopyLink = (u) => {
    const url = u.approval_url || (u.public_token ? `${window.location.origin}/approval/${u.public_token}` : null);
    if (!url) { toast.error("No approval link yet — send the proposal first"); return; }
    navigator.clipboard.writeText(url).then(() => toast.success("Deposit link copied"));
  };

  const handleOpenProposal = (u) => {
    window.open(`/upsells/${u.id}/proposal`, "_blank");
  };

  const handleSendFinalInvoice = async (u) => {
    if (!window.confirm("Send final balance invoice to client?")) return;
    try {
      await apiClient.post(`/change-orders/${u.id}/send-balance-invoice`);
      toast.success("Final invoice sent");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invoice");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-zinc-400 text-sm">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading upsells…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-zinc-50/40 min-h-screen">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-zinc-200 px-4 sm:px-6 py-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-[19px] font-light tracking-tight text-zinc-900">Upsells Pipeline</h1>
              <p className="text-[12.5px] text-zinc-500 mt-0.5">Track upsell proposals from submission to full payment.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} className="h-8 text-[12px] border-zinc-200 gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => navigate("/upsells/catalog-admin")} className="h-8 text-[12px] border-zinc-200 gap-1.5">
                <Settings className="w-3.5 h-3.5" /> Rate Card
              </Button>
            )}
            <Button size="sm" onClick={() => setAddOpen(true)} className="h-8 text-[12px] bg-violet-600 hover:bg-violet-700 text-white gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> New Upsell
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Upsells", value: upsells.length, sub: "all time", priceOnly: false },
            { label: "In Progress", value: inProgress, sub: "active pipeline", priceOnly: false },
            { label: "Pipeline Value", value: fmt$(totalRevenue), sub: "total scope", priceOnly: true },
            { label: "Deposits Collected", value: fmt$(depositCollected), sub: "revenue in", priceOnly: true },
          ].filter(s => !s.priceOnly || canShowPrice).map((s) => (
            <div key={s.label} className="rounded-xl border border-zinc-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium">{s.label}</div>
              <div className="text-[22px] font-light tabular-nums tracking-tight text-zinc-900 mt-1">{s.value}</div>
              <div className="text-[10.5px] text-zinc-400 mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pipeline columns ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-x-auto px-4 py-5">
        <div className="flex gap-3 min-w-max">
          {COLUMNS.map((col) => {
            const Icon = col.icon;
            const cards = grouped[col.key] || [];
            return (
              <div key={col.key} className="w-72 flex flex-col rounded-xl border border-zinc-200 bg-white overflow-hidden shrink-0">
                {/* Column header */}
                <div className={`px-4 py-3 border-b border-zinc-200 ${col.header} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-zinc-600" />
                    <span className="text-[13px] font-semibold text-zinc-800">{col.label}</span>
                  </div>
                  <span className="text-[11px] font-medium text-zinc-500 bg-white/70 rounded-full px-2 py-0.5 border border-zinc-200">{cards.length}</span>
                </div>

                {/* Cards */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[65vh]">
                  {cards.length === 0 ? (
                    <div className="text-center py-8 text-[12px] text-zinc-400">No upsells here</div>
                  ) : (
                    cards.map((u) => (
                      <UpsellCard
                        key={u.id}
                        upsell={u}
                        isAdmin={isAdmin}
                        isSales={isSales}
                        canShowPrice={canShowPrice}
                        expanded={expandedCard === u.id}
                        onToggle={() => setExpandedCard(expandedCard === u.id ? null : u.id)}
                        sending={!!sending[u.id]}
                        onSendProposal={() => handleSendProposal(u)}
                        onCopyLink={() => handleCopyLink(u)}
                        onOpenProposal={() => handleOpenProposal(u)}
                        onSendFinalInvoice={() => handleSendFinalInvoice(u)}
                        onRecordManualPayment={(paymentType) => setManualPay({ co: u, paymentType })}
                        canDelete={canDeleteAdditionalScope(u, { isFinance, user: me })}
                        onDelete={() => setDeleteTarget(u)}
                        canEdit={canEditAdditionalScope(u, { isFinance, user: me })}
                        onEdit={() => setEditTarget(u)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Manual payment recording ─────────────────────────────────────── */}
      <RecordManualPaymentDialog
        co={manualPay?.co || null}
        paymentType={manualPay?.paymentType || "deposit"}
        onClose={() => setManualPay(null)}
        onSaved={() => { setManualPay(null); load(); }}
      />

      {/* ── Delete confirmation ──────────────────────────────────────────── */}
      <DeleteAdditionalScopeDialog
        co={deleteTarget}
        showMoney={canShowPrice}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); setExpandedCard(null); load(); }}
      />

      {/* ── New Upsell Modal ─────────────────────────────────────────────── */}
      <AddChangeOrderModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={() => { setAddOpen(false); load(); }}
        forcedType="upsell"
      />

      {/* ── Edit — the same wizard, hydrated from an existing upsell. Keyed on
             the row id so switching cards remounts with fresh state. ─────── */}
      {editTarget && (
        <AddChangeOrderModal
          key={editTarget.id}
          open
          onOpenChange={(v) => { if (!v) setEditTarget(null); }}
          editingCo={editTarget}
          projectId={editTarget.project_id}
          projectName={editTarget.project_name}
          forcedType="upsell"
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function UpsellCard({ upsell: u, isAdmin, isSales, canShowPrice, expanded, onToggle, sending, onSendProposal, onCopyLink, onOpenProposal, onSendFinalInvoice, onRecordManualPayment, canDelete, onDelete, canEdit, onEdit }) {
  const total = u.final_client_price || u.total_amount || 0;
  const deposit = u.deposit_amount || 0;

  const statusLabel = STATUS_LABEL[u.status] || u.status;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white hover:border-zinc-300 transition-colors overflow-hidden">
      {/* Card body */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-3"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${columnFor(u.status).color}`}>
              {statusLabel}
            </span>
            {linkDaysRemaining(u) !== null && (
              <span className={`inline-flex items-center gap-1 text-[10px] ${linkDaysRemaining(u) <= 2 ? "text-red-600" : "text-zinc-400"}`}>
                <Clock className="w-2.5 h-2.5" />
                {linkDaysRemaining(u) === 0 ? "Expires today" : `${linkDaysRemaining(u)}d left`}
              </span>
            )}
          </div>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />}
        </div>

        <div className="text-[13px] font-semibold text-zinc-900 leading-tight">
          {u.title || u.category?.replace(/_/g, " ") || "Upsell"}
        </div>
        <div className="text-[11.5px] text-zinc-500 mt-0.5 truncate">
          {u.project_name || "—"}
        </div>

        <div className="flex items-center justify-between mt-2.5 gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-400">
            <Calendar className="w-3 h-3" />
            {fmtDate(u.created_at)}
          </div>
          {canShowPrice && total > 0 && (
            <div className="text-[13px] font-semibold tabular-nums text-zinc-900">{fmt$(total)}</div>
          )}
        </div>

        {u.sales_rep_name && (
          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-zinc-500">
            <User className="w-3 h-3" />
            {u.sales_rep_name}
          </div>
        )}

        {!u.client_email && (
          <div className="mt-2 flex items-center gap-1 text-[10.5px] text-amber-700 bg-amber-50 rounded px-2 py-1 border border-amber-200">
            <AlertCircle className="w-3 h-3 shrink-0" />
            No client email
          </div>
        )}
      </button>

      {/* Expanded details + actions */}
      {expanded && (
        <div className="border-t border-zinc-100 px-3 pb-3 pt-2 space-y-2">
          {/* Details */}
          {(u.description || u.line_items?.length > 0) && (
            <div className="text-[11.5px] text-zinc-600 space-y-1">
              {u.description && <p className="leading-relaxed">{u.description}</p>}
              {u.line_items?.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {u.line_items.map((li, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-zinc-500">
                      <span className="w-1 h-1 rounded-full bg-zinc-300 shrink-0" />
                      {li.item_name} {li.quantity > 1 ? `×${li.quantity}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {canShowPrice && (
            <div className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-[11.5px] space-y-0.5">
              {deposit > 0 && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Deposit (50%)</span>
                  <span className="font-medium tabular-nums">{fmt$(deposit)}</span>
                </div>
              )}
              {u.deposit_paid && (
                <div className="flex justify-between text-emerald-700">
                  <span>Deposit received</span>
                  <span className="font-medium">✓</span>
                </div>
              )}
              {total > 0 && (
                <div className="flex justify-between font-semibold border-t border-zinc-200 pt-1 mt-1">
                  <span className="text-zinc-700">Total</span>
                  <span className="tabular-nums">{fmt$(total)}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {/* Send / Resend proposal */}
            {["ready_to_send", "draft", "sent_to_client"].includes(u.status) && (
              <Button
                size="sm"
                variant={u.status === "sent_to_client" ? "outline" : "default"}
                className={`h-7 text-[11.5px] gap-1 ${u.status !== "sent_to_client" ? "bg-violet-600 hover:bg-violet-700 text-white" : "border-zinc-200"}`}
                onClick={onSendProposal}
                disabled={sending}
              >
                <Send className="w-3 h-3" />
                {u.status === "sent_to_client" ? "Resend" : "Send Proposal"}
              </Button>
            )}

            {/* Generate PDF */}
            <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1 border-zinc-200" onClick={onOpenProposal}>
              <FileText className="w-3 h-3" /> PDF
            </Button>

            {/* Copy deposit link */}
            {(u.approval_url || u.public_token) && (
              <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1 border-zinc-200" onClick={onCopyLink}>
                <Copy className="w-3 h-3" /> Deposit Link
              </Button>
            )}

            {/* Send final invoice (upsells after delivery) */}
            {u.status === "completed" && u.deposit_paid && !u.balance_paid && (
              <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={onSendFinalInvoice}>
                <DollarSign className="w-3 h-3" /> Final Invoice
              </Button>
            )}

            {/* Record manual deposit payment (received outside Stripe) */}
            {(isAdmin || isSales) && !u.deposit_paid && u.stripe_payment_status !== "paid" && ["ready_to_send", "sent_to_client", "client_approved", "invoice_sent"].includes(u.status) && (
              <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1 border-green-300 text-green-700 hover:bg-green-50" onClick={() => onRecordManualPayment("deposit")}>
                <DollarSign className="w-3 h-3" /> Record Payment
              </Button>
            )}
            {/* Record manual balance payment */}
            {(isAdmin || isSales) && u.status === "completed" && u.deposit_paid && !u.balance_paid && (
              <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1 border-green-300 text-green-700 hover:bg-green-50" onClick={() => onRecordManualPayment("balance")}>
                <DollarSign className="w-3 h-3" /> Record Balance
              </Button>
            )}

            {/* View client approval page */}
            {u.public_token && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11.5px] gap-1 text-zinc-500"
                onClick={() => window.open(`/approval/${u.public_token}`, "_blank")}
              >
                <ExternalLink className="w-3 h-3" /> Client View
              </Button>
            )}

            {/* Edit — reopens the intake wizard on this upsell. */}
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11.5px] gap-1"
                onClick={onEdit}
              >
                <Pencil className="w-3 h-3" /> Edit
              </Button>
            )}

            {/* Delete — for test rows and mistakes. Finance only. */}
            {canDelete && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11.5px] gap-1 text-red-600 hover:bg-red-50 hover:text-red-700 ml-auto"
                onClick={onDelete}
              >
                <Trash2 className="w-3 h-3" /> Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
